import { log, LogLevel } from '@/shared/logging'

/** When this tab last reloaded for a new release, so a code file that is really missing never reloads it in a loop. */
const reloadedAtKey = 'tms.shell.newReleaseReloadedAt'
const guardCheckKey = 'tms.shell.newReleaseGuardCheck'

/** A missing code file this soon after such a reload is not a new release: it fails as it is. */
const loopGuardMs = 60_000

/**
 * How long a load waits for its reload before failing anyway, as when the new page is slow to
 * answer. A page with unsaved input is never reloaded, so no one waits here after choosing to stay.
 */
const reloadWaitMs = 10_000

/** How long the check for a new release waits for index.html. */
const probeTimeoutMs = 5_000

// What each browser says when a dynamic import's file cannot be fetched, and Vite for a page's
// stylesheet. The same for a file that is gone, a lost network or a 5xx, so it only starts a check.
const missingCodeFileMessages = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'unable to preload css',
]

/** The check and reload under way, which every part failing meanwhile shares. */
let underWay: Promise<boolean> | null = null

/**
 * The fields the person typed, chose or edited something in: every `input` event's target, as a
 * native field or editor sends, and as a custom control such as the shared `Select` sends itself.
 * Held weakly, once each, and let go once gone from the page, so no field outlives its page here.
 */
const editedFields = new Set<WeakRef<Node>>()
const isTracked = new WeakSet<Node>()
let isWatchingEdits = false

/** Starts noting the fields the person edits, once, for {@link mayHoldUnsavedInput}. */
function watchEdits(): void {
  if (isWatchingEdits || typeof window === 'undefined') {
    return
  }
  isWatchingEdits = true
  window.addEventListener('input', (event) => {
    const field = event.target
    if (field instanceof Node && !isTracked.has(field)) {
      isEditedFieldShown()
      isTracked.add(field)
      editedFields.add(new WeakRef(field))
    }
  }, { capture: true })
}

/**
 * Whether a field the person edited is still on the page, where what they entered may still be.
 * Lets go of those gone with their page or dialog, and of what they held.
 */
function isEditedFieldShown(): boolean {
  let isShown = false
  for (const ref of editedFields) {
    const field = ref.deref()
    if (field?.isConnected === true) {
      isShown = true
    } else {
      editedFields.delete(ref)
      if (field !== undefined) {
        isTracked.delete(field)
      }
    }
  }
  return isShown
}

/**
 * Whether reloading could lose something the person entered: a page with unsaved changes asks
 * before it goes (a cancelable `beforeunload` it cancels, asked here without the browser's prompt),
 * or a field they edited is still shown, as on a page or in a dialog with no such prompt, which
 * would lose it silently. A page they have just moved to holds nothing of theirs yet.
 */
function mayHoldUnsavedInput(): boolean {
  if (isEditedFieldShown()) {
    return true
  }
  // Untrusted, so Datadog's page-exit listener ignores it; the app's prompts only cancel it.
  const asking = new Event('beforeunload', { cancelable: true })
  window.dispatchEvent(asking)
  return asking.defaultPrevented
}

/** Whether `error` is a page's code file failing to load, for whatever reason. */
export function isMissingCodeFile(error: unknown): boolean {
  const message = error instanceof Error ? error.message.toLowerCase() : ''
  return missingCodeFileMessages.some((text) => message.includes(text))
}

/** Reloads the page at the address it is on. An object, so tests can watch it. */
export const newRelease = {
  reload(): void {
    window.location.reload()
  },
}

/** The path of a page's entry script, which names its release, or null when it has none. */
function entryScriptPath(page: Document): string | null {
  const src = page.querySelector('script[type="module"][src]')?.getAttribute('src') ?? ''
  return src === '' ? null : new URL(src, window.location.href).pathname
}

/**
 * Whether the server now serves another release than this tab runs: its index.html, fetched
 * afresh, names another entry script. False when it cannot tell, such as offline, on a 5xx, or
 * with no entry script on either side, so only a real new release reloads.
 */
async function isNewReleaseLive(): Promise<boolean> {
  const running = entryScriptPath(document)
  if (!navigator.onLine || running === null) {
    return false
  }
  // A stalled answer, its body included, counts as no new release. A TimeoutError, so Datadog still logs it.
  const stalled = new AbortController()
  const timer = setTimeout(() => stalled.abort(new DOMException('index.html did not answer in time.', 'TimeoutError')), probeTimeoutMs)
  try {
    const response = await fetch('/index.html', { cache: 'no-store', credentials: 'same-origin', signal: stalled.signal })
    if (!response.ok) {
      return false
    }
    // An empty answer or a page with no entry script, such as a maintenance page, is no release.
    const served = entryScriptPath(new DOMParser().parseFromString(await response.text(), 'text/html'))
    return served !== null && served !== running
  } catch {
    return false
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Whether the loop guard allows a reload: none moments ago, and storage that can hold the guard,
 * proven by writing and removing a value of its size. With no guard to keep, no reload.
 */
function guardAllowsReload(): boolean {
  try {
    const sinceLast = Date.now() - Number(window.sessionStorage.getItem(reloadedAtKey))
    if (sinceLast >= 0 && sinceLast < loopGuardMs) {
      return false
    }
    window.sessionStorage.setItem(guardCheckKey, String(Date.now()))
    window.sessionStorage.removeItem(guardCheckKey)
    return true
  } catch {
    return false
  }
}

/**
 * Reloads the page once for a new release; false when it is not one, one reloaded moments ago, or
 * the page may hold the person's unsaved input, which then stays, with the failure and its retry.
 */
async function reloadOnce(error: Error): Promise<boolean> {
  // Asked again after the check, which can take seconds the person may spend typing.
  if (!guardAllowsReload() || mayHoldUnsavedInput() || !(await isNewReleaseLive()) || mayHoldUnsavedInput()) {
    return false
  }
  const stamp = () => {
    try {
      window.sessionStorage.setItem(reloadedAtKey, String(Date.now()))
    } catch {
      // Proven writable just before, so not expected.
    }
  }
  // Stamped as the reload starts, however long it takes. Should the browser still ask (a prompt
  // the check above could not see), only once the page goes, whenever that is, so a person who
  // stays is asked again next time. Added last, so it sees the others' preventDefault.
  const leaving = (event: BeforeUnloadEvent) => {
    if (event.defaultPrevented) {
      window.addEventListener('pagehide', stamp, { once: true })
    } else {
      stamp()
    }
  }
  window.addEventListener('beforeunload', leaving, { once: true })
  log(LogLevel.Warning, 'A code file of the page is gone after a new release: reloading the page once.', {
    error: `${error.name}: ${error.message}`,
  })
  newRelease.reload()
  await new Promise((resolve) => setTimeout(resolve, reloadWaitMs))
  window.removeEventListener('beforeunload', leaving)
  return true
}

/**
 * `load`, a page's or contribution's dynamic import, but when its code file is gone because a new
 * release replaced it, the page reloads once at the same address to pick up the new one, unless
 * that could lose the person's input. Any other failure fails as before.
 */
export function reloadingOnNewRelease<T>(load: () => Promise<T>): () => Promise<T> {
  watchEdits()
  return async () => {
    try {
      return await load()
    } catch (error) {
      if (error instanceof Error && isMissingCodeFile(error)) {
        underWay ??= reloadOnce(error).finally(() => {
          underWay = null
        })
        await underWay
      }
      throw error
    }
  }
}
